import type { OsWindow } from "./OsContext";

export const DEFAULT_OS_WINDOW_SIZE = { width: 1120, height: 700 };

/** Per-window cascade step — matches OsWindow title bar height so stacked headers stay visible. */
export const OS_WINDOW_CASCADE_STAGGER_PX = 44;

/** Desktop margin reserved so windows can cascade down/right without hitting edges. */
const CASCADE_LAYOUT_RESERVE_PX = OS_WINDOW_CASCADE_STAGGER_PX * 3;

/**
 * Margin left around a window that was filling the desktop when restored.
 * Matches the small shrink macOS shows when leaving a zoomed window.
 */
export const OS_WINDOW_RESTORE_INSET_PX = 56;

/** Stored frame is treated as full-bleed when it is within this many pixels of the desktop. */
const FULL_BLEED_SLACK_PX = 24;

const MIN_FLOATING_WIDTH = 200;
const MIN_FLOATING_HEIGHT = 200;

export interface ResolveOpenWindowSizeOptions {
  openWindowed?: boolean;
  appDefaultSize?: { width: number; height: number };
  /** Explicit size from programmatic openWindow callers (not dock/link defaults). */
  initialSize?: { width: number; height: number };
  desktopWidth: number;
  windowedDesktopHeight: number;
}

/** Standard floating size for new OS windows; openWindowed apps use their own defaultSize. */
export function resolveOpenWindowSize(
  options: ResolveOpenWindowSizeOptions,
): { width: number; height: number } {
  const {
    openWindowed,
    appDefaultSize,
    initialSize,
    desktopWidth,
    windowedDesktopHeight,
  } = options;

  const preferred =
    openWindowed && appDefaultSize
      ? appDefaultSize
      : initialSize ?? DEFAULT_OS_WINDOW_SIZE;

  const maxWidth = Math.max(
    MIN_FLOATING_WIDTH,
    desktopWidth - CASCADE_LAYOUT_RESERVE_PX,
  );
  const maxHeight = Math.max(
    MIN_FLOATING_HEIGHT,
    windowedDesktopHeight - CASCADE_LAYOUT_RESERVE_PX,
  );

  return {
    width: Math.min(preferred.width, maxWidth),
    height: Math.min(preferred.height, maxHeight),
  };
}

export interface ClampDesktopBoundsOptions {
  /**
   * Retained for callers. Maximized windows keep their stored frame, so this
   * no longer resizes them.
   */
  maximizedDesktopHeight?: number;
}

/** Diagonal cascade offset for newly opened windowed windows (Mac-style). */
export function computeCascadeWindowPosition(
  size: { width: number; height: number },
  desktopWidth: number,
  desktopHeight: number,
  basePosition?: { x: number; y: number } | null,
): { x: number; y: number } {
  const maxX = Math.max(0, desktopWidth - size.width);
  const maxY = Math.max(0, desktopHeight - size.height);

  let x: number;
  let y: number;

  if (basePosition) {
    x = basePosition.x + OS_WINDOW_CASCADE_STAGGER_PX;
    y = basePosition.y + OS_WINDOW_CASCADE_STAGGER_PX;
    if (x > maxX || y > maxY) {
      x = OS_WINDOW_CASCADE_STAGGER_PX;
      y = OS_WINDOW_CASCADE_STAGGER_PX;
    }
  } else {
    x = OS_WINDOW_CASCADE_STAGGER_PX;
    y = OS_WINDOW_CASCADE_STAGGER_PX;
  }

  return {
    x: Math.max(0, Math.min(x, maxX)),
    y: Math.max(0, Math.min(y, maxY)),
  };
}

/**
 * Frame to use when leaving the maximized state.
 * A smaller stored frame is kept. A full-bleed frame shrinks by a fixed inset
 * and recenters, so restore is visible instead of staying edge to edge.
 * Returns null when the stored frame should be kept as-is.
 */
export function resolveRestoreFrame(
  window: Pick<OsWindow, "position" | "size" | "minSize">,
  desktopWidth: number,
  desktopHeight: number,
): { position: { x: number; y: number }; size: { width: number; height: number } } | null {
  if (desktopWidth <= 0 || desktopHeight <= 0) return null;

  const fillsDesktop =
    window.size.width >= desktopWidth - FULL_BLEED_SLACK_PX &&
    window.size.height >= desktopHeight - FULL_BLEED_SLACK_PX;
  if (!fillsDesktop) return null;

  const minW = window.minSize?.width ?? MIN_FLOATING_WIDTH;
  const minH = window.minSize?.height ?? MIN_FLOATING_HEIGHT;
  const inset = OS_WINDOW_RESTORE_INSET_PX;
  const width = Math.round(
    Math.min(desktopWidth, Math.max(minW, desktopWidth - inset * 2)),
  );
  const height = Math.round(
    Math.min(desktopHeight, Math.max(minH, desktopHeight - inset * 2)),
  );

  return {
    position: {
      x: Math.round(Math.max(0, (desktopWidth - width) / 2)),
      y: Math.round(Math.max(0, (desktopHeight - height) / 2)),
    },
    size: { width, height },
  };
}

/** Clamp one window's position/size to the desktop rect; returns the same reference when unchanged. */
export function clampWindowToDesktopBounds(
  window: OsWindow,
  desktopWidth: number,
  desktopHeight: number,
  _options?: ClampDesktopBoundsOptions,
): OsWindow {
  if (window.isMinimized) {
    return window;
  }

  // Maximized windows paint at 100% of the desktop. Keep the stored frame so
  // restore can return to it instead of the full-bleed size.
  if (window.isMaximized) {
    return window;
  }

  const minW = window.minSize?.width ?? 200;
  const minH = window.minSize?.height ?? 40;

  let width = Math.min(window.size.width, desktopWidth);
  let height = Math.min(window.size.height, desktopHeight);
  width = Math.max(minW, width);
  height = Math.max(minH, height);
  if (width > desktopWidth) width = desktopWidth;
  if (height > desktopHeight) height = desktopHeight;

  const x = Math.round(
    Math.min(
      Math.max(0, window.position.x),
      Math.max(0, desktopWidth - width),
    ),
  );
  const y = Math.round(
    Math.min(
      Math.max(0, window.position.y),
      Math.max(0, desktopHeight - height),
    ),
  );

  if (
    width === window.size.width &&
    height === window.size.height &&
    x === window.position.x &&
    y === window.position.y
  ) {
    return window;
  }

  return {
    ...window,
    position: { x, y },
    size: { width, height },
  };
}

/** Returns window ids whose geometry changed after clamping. */
export function clampWindowsToDesktopBoundsList(
  windows: readonly OsWindow[],
  desktopWidth: number,
  desktopHeight: number,
  options?: ClampDesktopBoundsOptions,
): { windows: OsWindow[]; changedWindowIds: string[] } {
  const changedWindowIds: string[] = [];
  const next = windows.map((window) => {
    const clamped = clampWindowToDesktopBounds(
      window,
      desktopWidth,
      desktopHeight,
      options,
    );
    if (clamped !== window) {
      changedWindowIds.push(window.id);
    }
    return clamped;
  });
  return { windows: next, changedWindowIds };
}
